/**
 * Pure utility functions for traversing and mutating the FilterTree.
 * All mutations produce a new copy of the tree.
 */

export const generateId = () => Math.random().toString(36).substring(2, 10);

/**
 * Creates an empty root compound node.
 */
export const createEmptyTree = () => ({
    type: 'compound',
    id: generateId(),
    operator: 'AND',
    children: []
});

/**
 * Creates a new rule node based on a schema array.
 */
export const createDefaultRule = (schema = []) => {
    const firstField = schema[0];
    if (!firstField) {
        return {
            type: 'rule',
            id: generateId(),
            fieldId: '',
            fieldType: 'text',
            operator: 'contains',
            value: ''
        };
    }
    
    // Import here to avoid circular dependency if needed, or just hardcode standard default
    const defaultOp = {
        text: 'contains', number: 'equals', select: 'is', multi_select: 'contains',
        date: 'date_is', date_range: 'date_is', person: 'contains', checkbox: 'is_checked'
    }[firstField.type] || 'is';

    return {
        type: 'rule',
        id: generateId(),
        fieldId: firstField.id,
        fieldType: firstField.type,
        operator: defaultOp,
        value: ''
    };
};

export const createCompoundNode = (operator = 'AND') => ({
    type: 'compound',
    id: generateId(),
    operator,
    children: []
});

/**
 * @param {Object|null} tree 
 * @param {string} parentId 
 * @param {Object} node 
 * @returns {Object} new tree
 */
export const insertNode = (tree, parentId, node) => {
    if (!tree) return node;

    if (tree.id === parentId && (tree.type === 'compound' || tree.type === 'group')) {
        return { ...tree, children: [...tree.children, node] };
    }

    if (tree.type === 'compound' || tree.type === 'group') {
        return {
            ...tree,
            children: tree.children.map(child => insertNode(child, parentId, node))
        };
    }

    return tree;
};

/**
 * @param {Object|null} tree 
 * @param {string} nodeId 
 * @returns {Object|null} new tree
 */
export const removeNode = (tree, nodeId) => {
    if (!tree) return null;
    if (tree.id === nodeId) return null;

    if (tree.type === 'compound' || tree.type === 'group') {
        const filteredChildren = tree.children
            .map(child => removeNode(child, nodeId))
            .filter(Boolean);
            
        // If it's not the root node and it has no children left, remove it
        // We'll keep root node alive even if empty so users can add rules
        return { ...tree, children: filteredChildren };
    }

    return tree;
};

/**
 * @param {Object|null} tree 
 * @param {string} nodeId 
 * @param {Object} patch 
 * @returns {Object} new tree
 */
export const updateNode = (tree, nodeId, patch) => {
    if (!tree) return null;

    if (tree.id === nodeId) {
        return { ...tree, ...patch };
    }

    if (tree.type === 'compound' || tree.type === 'group') {
        return {
            ...tree,
            children: tree.children.map(child => updateNode(child, nodeId, patch))
        };
    }

    return tree;
};

/**
 * Recurse through the tree and replace a specific node with another node.
 * @param {Object|null} tree 
 * @param {string} nodeId 
 * @param {Object} newNode 
 * @returns {Object|null} new tree
 */
export const replaceNode = (tree, nodeId, newNode) => {
    if (!tree) return null;
    if (tree.id === nodeId) return newNode;

    if (tree.type === 'compound' || tree.type === 'group') {
        return {
            ...tree,
            children: tree.children.map(child => replaceNode(child, nodeId, newNode))
        };
    }

    return tree;
};

/**
 * @param {Object|null} tree 
 * @param {string} nodeId 
 * @returns {Object|null} node
 */
export const findNode = (tree, nodeId) => {
    if (!tree) return null;
    if (tree.id === nodeId) return tree;

    if (tree.type === 'compound' || tree.type === 'group') {
        for (const child of tree.children) {
            const found = findNode(child, nodeId);
            if (found) return found;
        }
    }

    return null;
};

/**
 * @param {Object|null} tree 
 * @returns {number} rule count
 */
export const countRules = (tree) => {
    if (!tree) return 0;
    if (tree.type === 'rule') return 1;
    if (tree.type === 'compound' || tree.type === 'group') {
        return tree.children.reduce((acc, child) => acc + countRules(child), 0);
    }
    return 0;
};

/**
 * @param {Object|null} tree 
 * @returns {Array} rules
 */
export const flattenRules = (tree) => {
    if (!tree) return [];
    if (tree.type === 'rule') return [tree];
    if (tree.type === 'compound' || tree.type === 'group') {
        return tree.children.flatMap(flattenRules);
    }
    return [];
};
